/**
 * Game Board UI Component
 */

import type { Player, GameState } from '../game/types.ts';

export interface GameBoardCallbacks {
  onCellClick: (position: number) => void;
}

/**
 * Create game board HTML element
 */
export function createGameBoard(
  gameState: GameState,
  callbacks: GameBoardCallbacks
): HTMLElement {
  const board = document.createElement('div');
  board.className = 'game-board';
  board.setAttribute('role', 'grid');
  board.setAttribute('aria-label', 'Tic-tac-toe game board');

  // Add cells
  for (let i = 0; i < 9; i++) {
    const cell = createCell(
      i,
      gameState.board[i] ?? null,
      gameState,
      callbacks
    );
    board.appendChild(cell);
  }

  return board;
}

/**
 * Create individual cell
 */
function createCell(
  position: number,
  value: Player | null,
  gameState: GameState,
  callbacks: GameBoardCallbacks
): HTMLElement {
  const cell = document.createElement('button');
  cell.className = 'game-cell';
  cell.type = 'button';

  // Accessibility attributes
  cell.setAttribute('role', 'gridcell');
  cell.setAttribute('aria-label', `Cell ${position + 1}`);

  if (value) {
    cell.textContent = value;
    cell.setAttribute('aria-label', `Cell ${position + 1}, ${value}`);
    cell.disabled = true;
    cell.classList.add('occupied');
  } else {
    cell.textContent = '';
    cell.setAttribute('aria-label', `Cell ${position + 1}, empty`);

    const canMove = gameState.isMyTurn && gameState.status === 'playing';

    cell.disabled = !canMove;

    if (!canMove) {
      cell.classList.add('disabled');
    }
  }

  // Click handler
  cell.addEventListener('click', () => {
    if (!cell.disabled && !value) {
      callbacks.onCellClick(position);
    }
  });

  // Keyboard navigation
  cell.addEventListener('keydown', event => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (!cell.disabled && !value) {
        callbacks.onCellClick(position);
      }
    }
  });

  return cell;
}

/**
 * Update existing game board
 */
export function updateGameBoard(
  boardElement: HTMLElement,
  gameState: GameState,
  _callbacks: GameBoardCallbacks
): void {
  const cells = boardElement.querySelectorAll('.game-cell');

  cells.forEach((cell, index) => {
    const button = cell as HTMLButtonElement;
    const value = gameState.board[index];

    if (value) {
      button.textContent = value;
      button.setAttribute('aria-label', `Cell ${index + 1}, ${value}`);
      button.disabled = true;
      button.classList.add('occupied');
      button.classList.remove('disabled');
    } else {
      button.textContent = '';
      button.setAttribute('aria-label', `Cell ${index + 1}, empty`);
      button.classList.remove('occupied');

      const canMove = gameState.isMyTurn && gameState.status === 'playing';

      button.disabled = !canMove;

      if (!canMove) {
        button.classList.add('disabled');
      } else {
        button.classList.remove('disabled');
      }
    }
  });
}
